/**
 * Seconds left until a moment, ticking once a second — and only while there
 * is something to count. When the moment has passed (or there is none) it
 * returns 0 and no timer runs, so an idle page re-renders nothing.
 *
 * `serverOffsetMs` is server time minus this device's time: the claim
 * cooldown is decided by the server's clock, and a laptop whose clock is a
 * minute off should not show a button as ready that the server will refuse.
 */
import { useEffect, useState } from "react";

const left = (until, offset) => {
  if (!until) return 0;
  const ms = new Date(until).getTime() - (Date.now() + offset);
  return Number.isFinite(ms) && ms > 0 ? Math.ceil(ms / 1000) : 0;
};

export default function useCountdown(until, serverOffsetMs = 0) {
  const [seconds, setSeconds] = useState(() => left(until, serverOffsetMs));
  useEffect(() => {
    const now = left(until, serverOffsetMs);
    setSeconds(now);
    if (!now) return undefined;
    const id = setInterval(() => {
      const next = left(until, serverOffsetMs);
      setSeconds(next);
      if (!next) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [until, serverOffsetMs]);
  return seconds;
}

/** `Date.now()` that advances every `everyMs`, for "waiting 4 min" labels. */
export function useNow(everyMs = 1000, enabled = true) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs, enabled]);
  return now;
}
